import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Calendar, Phone, Building2, User, Tag, Clock, ChevronLeft, ChevronRight, RefreshCw, AlertCircle, FileSpreadsheet, FileText, Copy, Receipt, MapPin, X } from 'lucide-react';
import { tallyApi } from '../services/api';
import UpdateExpiryModal from '../components/Tally/UpdateExpiryModal';
import QuickInvoiceModal from '../components/Tally/QuickInvoiceModal';
import UpdateHistoryModal, { HistoryEntry } from '../components/UpdateHistoryModal';
import { useToast } from '../components/Toast/Toast';
import { useAuth } from '../context/AuthContext';

interface ExpiryRenewPageProps {
    customerType: 'our' | 'not_our';
}

const ExpiryRenewPage: React.FC<ExpiryRenewPageProps> = ({ customerType }) => {
    const { showError, showSuccess } = useToast();
    const { canCheckPermission, isAdmin } = useAuth();
    const navigate = useNavigate();
    // Pincode drill-through from the Pincode-wise summary. When present we show
    // every matching customer (dates cleared) so the list matches the count.
    const [searchParams, setSearchParams] = useSearchParams();
    const pincodeFilter = searchParams.get('pincode') || '';

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [search, setSearch] = useState('');
    const [activeStatus, setActiveStatus] = useState('All');
    const [allCount, setAllCount] = useState(0);
    // Progress of the "Update All" Tally-API sync ({done,total} while running, null when idle).
    const [updateAllProgress, setUpdateAllProgress] = useState<{ done: number; total: number } | null>(null);

    // Group / Sub-Group filter. `groups` is the flat ledger-group tree; the
    // Group dropdown lists the primary groups (children of a root like Current
    // Assets, e.g. Sundry Debtors) and the Sub-Group dropdown cascades to the
    // selected group's children (the person sub-groups).
    const [groups, setGroups] = useState<Array<{ id: number; name: string; parent_id: number | null }>>([]);
    const [groupId, setGroupId] = useState<number | ''>('');
    useEffect(() => {
        tallyApi.getLedgerGroups().then(r => { if ((r as any)?.success) setGroups((r as any).data || []); }).catch(() => {});
    }, []);
    // ONE hierarchy dropdown: the whole group tree flattened + indented, so
    // Group and Sub-Group live in a single control. Picking any node filters to
    // that node + all its descendants (the backend expands the subtree).
    const flatGroups = useMemo(() => {
        const ids = new Set(groups.map(g => g.id));
        const byParent = new Map<number | null, Array<{ id: number; name: string; parent_id: number | null }>>();
        for (const g of groups) { const k = g.parent_id; if (!byParent.has(k)) byParent.set(k, []); byParent.get(k)!.push(g); }
        Array.from(byParent.values()).forEach(arr => arr.sort((a, b) => a.name.localeCompare(b.name)));
        const out: Array<{ id: number; label: string; depth: number }> = [];
        const walk = (nodes: any[], depth: number, seen: Set<number>) => {
            for (const n of nodes) {
                if (seen.has(n.id)) continue; seen.add(n.id);
                out.push({ id: n.id, label: `${'   '.repeat(depth)}${depth ? '└ ' : ''}${n.name}`, depth });
                walk(byParent.get(n.id) || [], depth + 1, seen);
            }
        };
        // Roots = no parent OR parent outside the visible (scoped) set — so a
        // group-scoped user's subtree renders even though its top parent is hidden.
        const roots = groups.filter(g => g.parent_id == null || !ids.has(g.parent_id)).sort((a, b) => a.name.localeCompare(b.name));
        walk(roots, 0, new Set());
        return out;
    }, [groups]);

    
    // Default Dates: First day of current month to last day of current month (Corrected for timezone)
    const getDefaultDates = () => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        // Use local components to avoid UTC shifting
        const formatLocal = (d: Date) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        return {
            from: formatLocal(first),
            to: formatLocal(last)
        };
    };


    const [dateFrom, setDateFrom] = useState(pincodeFilter ? '' : getDefaultDates().from);
    const [dateTo, setDateTo] = useState(pincodeFilter ? '' : getDefaultDates().to);
    
    const [selectedTally, setSelectedTally] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [quickInvoiceTarget, setQuickInvoiceTarget] = useState<{ customerId: number; companyName: string; tallyserial?: string; tallyFlavourId?: number } | null>(null);

    // Status/call history popup: click the ×N badge → full interaction log
    const [historyTarget, setHistoryTarget] = useState<{ title: string } | null>(null);
    const [historyRows, setHistoryRows] = useState<HistoryEntry[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const openCallHistory = async (item: any) => {
        setHistoryTarget({ title: `${item.company_name || ''} — ${item.tallyserial}` });
        setHistoryRows([]);
        setHistoryLoading(true);
        try {
            const res = await tallyApi.getExpiryCallHistory(item.tallyserial);
            if (res.success) setHistoryRows((res.data || []).map((r: any) => ({
                when: r.created_at, status: r.expiry_status,
                next_date: r.next_follow_date, remark: r.remarks, by: r.updated_by,
                // Who answered, when the caller picked a contact.
                person: r.contact_person, phone: r.contact_mobile,
            })));
        } catch { /* modal shows empty state */ }
        finally { setHistoryLoading(false); }
    };

    const statuses = [
        { name: 'All', color: '#28a745' }, // Green
        { name: 'Pending', color: '#007bff' }, // Blue
        { name: 'Pending-Order', color: '#fd7e14' }, // Orange
        { name: 'Interested', color: '#00ccff' }, // Light Blue
        { name: 'Not-Interested', color: '#6610f2' }, // Purple
        { name: 'Call-Back Later', color: '#ffc107' }, // Yellow
        { name: 'Wrong-No', color: '#e83e8c' }, // Pink
        { name: 'Not-Responding', color: '#20c997' }, // Greenish
        { name: 'Business-Closed', color: '#dc3545' }, // Red
        { name: 'Software-Change', color: '#ff8c00' }, // Dark Orange
        { name: 'Not In Use', color: '#c0392b' }, // Dark Red
        { name: 'Reseller', color: '#922b21' }, // Darker Red
        // Auto-set only — stamped when a voucher is created from this report
        // (no manual way to pick it in the renewal-call status dropdown)
        { name: 'Billed', color: '#16a34a' } // Green
    ];

    const [activeSearch, setActiveSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setActiveSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const response = await tallyApi.getExpiryReport({
                customer_type: customerType,
                expiry_status: activeStatus,
                search: activeSearch,
                page,
                limit,
                date_from: dateFrom,
                date_to: dateTo,
                pincode: pincodeFilter || undefined,
                group_id: groupId ? Number(groupId) : undefined,
            });
            setData(response.data);
            setTotal(response.total);
            setAllCount(response.allCount || response.total);
            setStatusCounts(response.statusCounts);
        } catch (error: any) {
            showError('Error', error.message || 'Failed to fetch report');
        } finally {
            setLoading(false);
        }
    }, [customerType, activeStatus, activeSearch, page, limit, dateFrom, dateTo, pincodeFilter, groupId, showError]);

    // "Update All" — pulls the latest expiry/edition for EVERY serial that
    // matches the current filters (all pages, not just the visible page) from
    // the Tally licensing API (same endpoint as the auto-sync and the
    // CustomerSearch "Tally API" button), one at a time with gentle pacing to
    // avoid rate limits, then refreshes the report.
    const handleUpdateAll = async () => {
        if (updateAllProgress) return; // already running
        setUpdateAllProgress({ done: 0, total: 0 });
        // Step 1: fetch the full filtered set (all pages) to collect every serial.
        let serials: string[] = [];
        try {
            const full = await tallyApi.getExpiryReport({
                customer_type: customerType,
                expiry_status: activeStatus,
                search: activeSearch,
                page: 1,
                limit: 100000,
                date_from: dateFrom,
                date_to: dateTo,
                pincode: pincodeFilter || undefined,
                group_id: groupId ? Number(groupId) : undefined,
            });
            serials = Array.from(new Set(((full.data as any[]) || []).map((r: any) => r.tallyserial).filter(Boolean))) as string[];
        } catch (e: any) {
            setUpdateAllProgress(null);
            showError('Tally API', e?.message || 'Failed to load serials');
            return;
        }
        if (serials.length === 0) { setUpdateAllProgress(null); showError('Tally API', 'No serials to update'); return; }
        // Step 2: sync each serial sequentially.
        setUpdateAllProgress({ done: 0, total: serials.length });
        let updated = 0, failed = 0;
        for (let i = 0; i < serials.length; i++) {
            try {
                const res: any = await tallyApi.syncSerial(serials[i]);
                if (res?.success) { if ((res.changes || []).length > 0) updated++; }
                else failed++;
            } catch { failed++; }
            setUpdateAllProgress({ done: i + 1, total: serials.length });
            await new Promise(r => setTimeout(r, 250)); // pace the external API
        }
        setUpdateAllProgress(null);
        if (failed > 0) {
            showError('Tally API', `Done — ${updated} updated, ${failed} failed of ${serials.length}`);
        } else {
            showSuccess('Tally API', `Done — ${updated} updated, ${serials.length - updated} already current`);
        }
        await fetchReport();
    };

    // Clear the pincode drill-through filter and restore the default month range.
    const clearPincode = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('pincode');
        setSearchParams(next, { replace: true });
        setDateFrom(getDefaultDates().from);
        setDateTo(getDefaultDates().to);
        setPage(1);
    };

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // Auto-sync stale serials with the Tally API in the background, then refetch.
    // "Stale" = never checked OR checked more than 7 days ago. Limited to 10 per page
    // load to avoid hammering the Tally API. Sequential with a small delay between
    // calls so we don't trip rate limits.
    const [autoSyncing, setAutoSyncing] = useState(false);
    const autoSyncedKey = useRef<string>('');
    useEffect(() => {
        if (loading || data.length === 0 || autoSyncing) return;
        const key = `${customerType}|${activeStatus}|${page}|${activeSearch}`;
        if (autoSyncedKey.current === key) return; // already synced this view

        const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
        const stale = data.filter((r: any) => {
            if (!r.tallyserial) return false;
            if (!r.cron_date) return true;                 // never synced
            const ts = new Date(r.cron_date).getTime();
            return isNaN(ts) || Date.now() - ts > SEVEN_DAYS_MS;
        }).slice(0, 10);

        if (stale.length === 0) return;
        autoSyncedKey.current = key;

        (async () => {
            setAutoSyncing(true);
            for (const row of stale) {
                try {
                    await tallyApi.syncSerial(row.tallyserial);
                    await new Promise(r => setTimeout(r, 800));
                } catch { /* ignore individual failures */ }
            }
            setAutoSyncing(false);
            // Do NOT auto-refetch — user can manually refresh
        })();
    }, [loading, data, customerType, activeStatus, page, activeSearch]);

    const handleSearch = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setPage(1);
        setActiveSearch(search);
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => {
            showSuccess('Copied', `${label} copied to clipboard`);
        }).catch(() => {
            showError('Error', 'Failed to copy to clipboard');
        });
    };

    const handleExport = async (type: 'excel' | 'pdf') => {
        try {
            if (type === 'excel') {
                const { utils, writeFile } = await import('xlsx');
                
                showSuccess('Exporting', 'Fetching all matching records...');
                
                // Fetch ALL matching activities for export (no pagination)
                const response = await tallyApi.getExpiryReport({
                    customer_type: customerType,
                    expiry_status: activeStatus,
                    search,
                    page: 1,
                    limit: 10000, // Large limit for export
                    date_from: dateFrom,
                    date_to: dateTo,
                    group_id: groupId ? Number(groupId) : undefined,
                });

                if (!response.data || response.data.length === 0) {
                    showError('Export Failed', 'No data found to export');
                    return;
                }

                const exportData = response.data.map((item: any, idx: number) => ({
                    'Sr No': idx + 1,
                    'Tally Serial': item.tallyserial,
                    'Expiry Date': item.tallyexpirydate ? new Date(item.tallyexpirydate).toLocaleDateString('en-GB') : 'N/A',
                    'Company Name': item.company_name,
                    'Group': item.ledger_group_name || '',
                    'Sub Group': item.ledger_subgroup_name || '',
                    'Person': item.customer_person || 'N/A',

                    'Mobile': item.customer_mobile || 'N/A',
                    'Flavor': item.flavor_name || 'Silver',
                    'Next Date': item.next_follow_date ? new Date(item.next_follow_date).toLocaleDateString('en-GB') : '-',
                    'Remark': item.expiry_remarks || '-'
                }));

                const ws = utils.json_to_sheet(exportData);
                const wb = utils.book_new();
                utils.book_append_sheet(wb, ws, 'Expiry Report');
                writeFile(wb, `Tally_Expiry_Report_${activeStatus}_${new Date().toISOString().split('T')[0]}.xlsx`);
                showSuccess('Success', 'Excel export complete');
            } else {
                // simple PDF export using window.print() or just notify it's coming
                showSuccess('Exporting', 'Preparing PDF for print...');
                window.print();
            }
        } catch (error: any) {
            showError('Export Failed', error.message || 'Error generating export');
        }
    };


    return (
        <div className="flex flex-col h-full bg-[#f8f9fa]">
            {/* Header Section */}
            <div className="bg-white border-b border-gray-200 px-6 py-3">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-bold text-gray-800">
                            {customerType === 'our' ? 'Our Tally' : 'Not Our Tally'} Expiry Report
                        </h1>
                        <span className="text-[13px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded italic">
                            {dateFrom} to {dateTo} - {activeStatus}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleUpdateAll}
                            disabled={!!updateAllProgress || loading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded shadow-sm transition-colors text-[13px] font-bold"
                            title="Fetch the latest expiry / edition for every serial on this page from the Tally API"
                        >
                            <RefreshCw className={`h-4 w-4 ${updateAllProgress ? 'animate-spin' : ''}`} />
                            {updateAllProgress
                                ? (updateAllProgress.total ? `Updating ${updateAllProgress.done}/${updateAllProgress.total}…` : 'Loading…')
                                : 'Update All (Tally)'}
                        </button>
                        {isAdmin() && (<>
                            <button
                                onClick={() => handleExport('excel')}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-[13px] font-bold text-gray-700"
                            >
                                <FileSpreadsheet className="h-4 w-4 text-green-700" />
                                Excel
                            </button>
                            <button
                                onClick={() => handleExport('pdf')}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-[13px] font-bold text-gray-700"
                            >
                                <FileText className="h-4 w-4 text-red-700" />
                                PDF
                            </button>
                        </>)}
                    </div>
                </div>


                {/* Filters and Status Row - Ultra Compact */}
                <div className="flex flex-col gap-3">
                    {/* Status Tags */}
                    <div className="flex flex-wrap items-center gap-1.5">
                        {statuses.map(s => (
                            <button
                                key={s.name}
                                onClick={() => { setActiveStatus(s.name); setPage(1); }}
                                style={{ 
                                    backgroundColor: activeStatus === s.name ? s.color : '#fff',
                                    borderColor: s.color,
                                    color: activeStatus === s.name ? '#fff' : s.color
                                }}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all flex items-center gap-1 ${activeStatus === s.name ? 'shadow-sm' : 'hover:bg-opacity-10'}`}
                            >
                                {s.name} <span className="opacity-80 text-[10px]">({s.name === 'All' ? allCount : (statusCounts[s.name] || 0)})</span>

                            </button>
                        ))}
                    </div>

                    {/* Filter Inputs Bar */}
                    <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-4 bg-gray-50 p-2 rounded-lg border border-gray-100">
                        {pincodeFilter && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-[12px] font-semibold border border-blue-200">
                                <MapPin size={12} /> Pincode: {pincodeFilter}
                                <button onClick={clearPincode} className="ml-0.5 hover:text-blue-900" title="Clear pincode filter"><X size={13} /></button>
                            </span>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                            <Calendar className="h-4 w-4 text-gray-400 hidden md:block" />
                            <div className="flex items-center gap-1">
                                <input
                                    type="date"
                                    className="px-2 py-1.5 border border-gray-300 rounded text-[12px] md:text-[13px] focus:ring-1 focus:ring-green-500 w-[130px] md:w-36 outline-none"
                                    value={dateFrom}
                                    onChange={e => setDateFrom(e.target.value)}
                                />
                                <span className="text-gray-400 text-xs">to</span>
                                <input
                                    type="date"
                                    className="px-2 py-1.5 border border-gray-300 rounded text-[12px] md:text-[13px] focus:ring-1 focus:ring-green-500 w-[130px] md:w-36 outline-none"
                                    value={dateTo}
                                    onChange={e => setDateTo(e.target.value)}
                                />
                            </div>
                            <button
                                onClick={handleSearch}
                                className="px-4 py-1.5 bg-green-600 text-white rounded text-[12px] md:text-[13px] font-bold hover:bg-green-700 transition-colors shadow-sm"
                            >
                                Update
                            </button>
                            <button
                                onClick={() => fetchReport()}
                                className="p-1.5 border border-gray-300 rounded hover:bg-gray-100 transition-colors text-gray-500"
                                title="Refresh"
                            >
                                <RefreshCw size={14} />
                            </button>
                        </div>

                        <div className="h-px md:h-6 w-full md:w-px bg-gray-300"></div>

                        {/* Group / Sub-Group filter — one hierarchy dropdown */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <select
                                value={groupId}
                                onChange={e => { setGroupId(e.target.value ? Number(e.target.value) : ''); setPage(1); }}
                                title="Filter by Group / Sub Group"
                                className="px-2 py-1.5 border border-gray-300 rounded text-[12px] md:text-[13px] bg-white outline-none focus:ring-1 focus:ring-green-500 max-w-[230px]"
                            >
                                <option value="">All Groups</option>
                                {flatGroups.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                            </select>
                            {groupId !== '' && (
                                <button
                                    onClick={() => { setGroupId(''); setPage(1); }}
                                    className="p-1 text-gray-400 hover:text-gray-700"
                                    title="Clear group filter"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <div className="h-px md:h-6 w-full md:w-px bg-gray-300"></div>

                        <div className="flex-1 flex items-center gap-2">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Search details..."
                                    className="pl-8 pr-3 py-1.5 border border-sky-200 rounded text-[12px] md:text-[13px] outline-none focus:ring-1 focus:ring-sky-500 w-full"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="flex-1 overflow-auto px-2 md:px-6 py-2 md:py-4">
                <div className="bg-white border border-gray-300 rounded-sm shadow-sm">
                    <table className="w-full text-left border-collapse table-fixed border-hidden">
                        <thead>
                            <tr className="bg-[#f1f5f9] border-b border-gray-300">
                                <th className="px-2 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[45px] text-center">Sr</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[110px]">Tally Serial</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[100px]">Expiry Date</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 min-w-[200px]">Company Name</th>

                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[130px]">Group</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[130px]">Sub Group</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[100px]">Person</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[110px]">Mobile</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[90px]">Flavor</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[100px]">Next Date</th>
                                <th className="px-3 py-2 text-[12px] font-bold text-gray-800 border border-gray-300 w-[150px]">Remark</th>
                            </tr>
                        </thead>

                        <tbody className="bg-white">
                            {loading ? (
                                Array.from({ length: 15 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 11 }).map((_, j) => (
                                            <td key={j} className="px-3 py-2 border border-gray-300 bg-gray-50/20"><div className="h-4 bg-gray-100 rounded w-full animate-pulse"></div></td>
                                        ))}
                                    </tr>
                                ))
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin() ? 13 : 11} className="px-4 py-12 text-center bg-gray-50/10 border border-gray-300">
                                        <div className="flex flex-col items-center gap-2 text-gray-400">
                                            <AlertCircle className="h-10 w-10 opacity-20" />
                                            <p className="text-sm">No records found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.map((item, idx) => (
                                <tr key={item.id} className="hover:bg-blue-50/30 transition-colors">
                                    <td className="px-2 py-1.5 text-[12px] text-gray-600 border border-gray-300 text-center">{(page - 1) * limit + idx + 1}</td>
                                    <td className="px-3 py-1.5 border border-gray-300 group/cell">
                                        <div className="flex items-center justify-between gap-1">
                                            <button 
                                                onClick={() => { setSelectedTally(item); setIsModalOpen(true); }}
                                                className="text-blue-700 font-bold hover:underline text-[12px] whitespace-nowrap"
                                            >
                                                {item.tallyserial}
                                            </button>
                                            {canCheckPermission(customerType === 'our' ? 'expiry_renew_our' : 'expiry_renew_not_our', 'copy') && (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(item.tallyserial, 'Serial'); }}
                                                    className="p-1 text-gray-300 hover:text-blue-500 opacity-0 group-hover/cell:opacity-100 transition-opacity"
                                                    title="Copy Serial"
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] text-gray-700 border border-gray-300 whitespace-nowrap overflow-hidden">
                                        {item.tallyexpirydate ? (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setQuickInvoiceTarget({
                                                        customerId: item.customerid, companyName: item.company_name,
                                                        tallyserial: item.tallyserial,
                                                        tallyFlavourId: item.tallyflavor ? Number(item.tallyflavor) : undefined,
                                                    });
                                                }}
                                                className="hover:underline text-blue-700 hover:text-blue-900 cursor-pointer text-left"
                                                title={item.billed_voucher_id ? `Billed — ${item.billed_vch_no || 'voucher created'}. Click to create another.` : 'Create Sales Tax Invoice for this customer'}
                                            >
                                                <span className="flex items-center gap-1">
                                                    <Receipt className="h-3 w-3 flex-shrink-0" />
                                                    {new Date(item.tallyexpirydate).toLocaleDateString('en-GB')}
                                                </span>
                                                {item.billed_voucher_id && (
                                                    <span className="mt-0.5 inline-block text-[9px] leading-none font-bold uppercase bg-green-100 text-green-700 border border-green-300 rounded px-1 py-0.5">Billed</span>
                                                )}
                                            </button>
                                        ) : 'N/A'}
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] font-bold text-gray-900 border border-gray-300 truncate group/company">
                                        <div className="flex items-center justify-between gap-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate('/search', { state: { customerId: item.customerid } }); }}
                                                className="hover:underline text-blue-700 hover:text-blue-900 cursor-pointer text-left truncate"
                                                title={item.company_name}
                                            >
                                                {item.company_name}
                                            </button>
                                            {canCheckPermission(customerType === 'our' ? 'expiry_renew_our' : 'expiry_renew_not_our', 'copy') && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(item.company_name, 'Company Name'); }}
                                                    className="p-1 text-gray-300 hover:text-blue-500 opacity-0 group-hover/company:opacity-100 transition-opacity"
                                                    title="Copy Company"
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>
                                    </td>

                                    <td className="px-3 py-1.5 text-[12px] text-gray-700 border border-gray-300 truncate" title={item.ledger_group_name}>
                                        {item.ledger_group_name || '—'}
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] text-gray-700 border border-gray-300 truncate" title={item.ledger_subgroup_name}>
                                        {item.ledger_subgroup_name || '—'}
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] text-gray-700 border border-gray-300 truncate" title={item.customer_person}>
                                        {item.customer_person || 'N/A'}
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] text-gray-700 border border-gray-300 whitespace-nowrap group/mobile">
                                        <div className="flex items-center justify-between gap-1">
                                            <span>{item.customer_mobile || 'N/A'}</span>
                                            {item.customer_mobile && (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(item.customer_mobile, 'Mobile'); }}
                                                    className="p-1 text-gray-300 hover:text-blue-500 opacity-0 group-hover/mobile:opacity-100 transition-opacity"
                                                    title="Copy Mobile"
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] text-gray-700 border border-gray-300">
                                        {item.flavor_name || 'Silver'}
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] text-gray-700 border border-gray-300 overflow-hidden">
                                        <span className="whitespace-nowrap">{item.next_follow_date ? new Date(item.next_follow_date).toLocaleDateString('en-GB') : '-'}</span>
                                        {Number(item.call_update_count) > 0 && (
                                            <span className="mt-0.5 block">
                                                <button onClick={(e) => { e.stopPropagation(); openCallHistory(item); }}
                                                    className="inline-block text-[9px] leading-none font-bold bg-blue-100 text-blue-700 border border-blue-300 rounded-full px-1.5 py-0.5 hover:bg-blue-200 cursor-pointer"
                                                    title={`View ${item.call_update_count} update(s) on this serial`}>
                                                    ×{item.call_update_count}
                                                </button>
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-1.5 text-[12px] text-gray-600 border border-gray-300 truncate" title={item.expiry_remarks}>
                                        {item.expiry_remarks || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>

                    </table>

                    {/* Footer Info */}
                    <div className="px-4 py-2 bg-[#f8f9fa] border-t border-gray-300 flex items-center justify-between">
                         <p className="text-[13px] text-gray-600">
                            Showing <span className="font-bold">{(page - 1) * limit + 1}</span> to <span className="font-bold">{Math.min(page * limit, total)}</span> of <span className="font-bold">{total}</span> results
                        </p>
                        
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <span className="text-[13px] font-bold px-3">Page {page} of {Math.ceil(total / limit) || 1}</span>
                            <button
                                onClick={() => setPage(p => (p * limit < total ? p + 1 : p))}
                                disabled={page * limit >= total}
                                className="p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Update Modal */}
            <UpdateExpiryModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={fetchReport}
                data={selectedTally}
            />

            {/* Quick Sales Invoice Modal */}
            {quickInvoiceTarget && (
                <QuickInvoiceModal
                    isOpen={true}
                    onClose={() => setQuickInvoiceTarget(null)}
                    onSuccess={fetchReport}
                    data={quickInvoiceTarget}
                />
            )}

            {historyTarget && (
                <UpdateHistoryModal
                    isOpen={true}
                    onClose={() => setHistoryTarget(null)}
                    title={historyTarget.title}
                    subtitle="Renewal call history — newest first"
                    loading={historyLoading}
                    entries={historyRows}
                />
            )}
        </div>
    );
};

export default ExpiryRenewPage;
